// React
import { useCallback } from 'react'

// i18n
import { useTranslation } from 'react-i18next'

// Interfaces
import { IDropdown } from 'interfaces/common'

const useValidation = () => {
  // Hook
  const { t } = useTranslation()

  /**
   * @description Render required validation
   *
   * @param {string} field
   * @param {any} options
   *
   * @return {object} object
   */
  const validation_required = useCallback(
    (field: string, options?: { customRequired: any }): { required: boolean; message: string } => {
      return {
        required: options?.customRequired?.toString() ? options?.customRequired : true,
        message: `${t(field)} ${t('app.validations.required')}`,
      }
    },
    [t],
  )

  /**
   * @description Render email must be valid
   *
   * @return {object} object
   */
  const validation_validEmail = useCallback((): { type: 'email'; message: string } => {
    return { type: 'email', message: `${t('app.validations.email')}` }
  }, [t])

  /**
   * @description Render only number validation
   *
   * @param {string} value
   *
   * @return {boolean} boolean
   */
  const validation_onlyNumber = useCallback((value: string): boolean => {
    const onlyNumber = /^[0-9\b]+$/

    return onlyNumber.test(value)
  }, [])

  /**
   * @description Render alpha numeric validation
   *
   * @param {string} value
   *
   * @return {boolean} boolean
   */
  const validation_alphaNumeric = useCallback((value: string): boolean => {
    const alphaNumeric = /^[a-zA-Z0-9 /]+$/

    return alphaNumeric.test(value)
  }, [])

  /**
   * @description Render phone number validation
   *
   * @param {string} value
   *
   * @return {boolean} boolean
   */
  const validation_phoneNumber = useCallback((value: any): boolean => {
    const phoneNumber = /^[0-9|-]+$/

    return phoneNumber.test(value)
  }, [])

  /**
   * @description Render Code validation
   *
   * @param {string} value
   *
   * @return {boolean} boolean
   */
  const validation_code = useCallback((value: any): boolean => {
    const code = /^[0-9|A-Z|.|/]+$/

    return code.test(value)
  }, [])

  /**
   * @description Render dropdown unique validation
   *
   * @param {any[]} currentValues
   * @param {string} currentValue
   * @param {IDropdown[]} dropdown
   *
   * @return {Promise<any> | undefined} Promise<any> | undefined
   */
  const validation_dropdownUnique = useCallback(
    (currentValues: any[], currentValue: string, dropdown: IDropdown[]): Promise<any> | undefined => {
      if (currentValues.filter((value: any) => value.includes(currentValue))?.length > 1) {
        return Promise.reject(
          new Error(`${dropdown?.find(role => role.value === currentValue)?.label || ''} ${t('app.validations.unique')}`),
        )
      }
    },
    [t],
  )

  /**
   * @description Render only decimal or integer validation
   *
   * @param {string} value
   *
   * @return {boolean} boolean
   */
  const validation_decimal = useCallback((value: string): boolean => {

    const decimal = /^[0-9]+\.?[0-9]*$/

    return decimal.test(value)
  }, [])

  /**
   * @description Render validations form
   *
   * @param {string[]} validations
   * @param {string} fieldName
   * @param {function} callback
   *
   * @return {any} any
   */
  const validation_validate = useCallback(
    (validations: string[], fieldName: string, callback?: (value: any, validatorCallback: (value: any) => any) => void): any => {
      const rules = []

      // Required validation
      if (validations.includes('required')) rules.push(validation_required(t(fieldName)))
      if (validations.includes('email')) rules.push(validation_validEmail())

      // Validate the numeric length

      // Push custom validator
      rules.push({
        validator: (_: any, value: any, validatorCallback: (value: any) => any) => {
          // Numeric
          if (validations.includes('numeric')) {
            // Accept only number
            if (value && !validation_onlyNumber(value))
              return Promise.reject(new Error(`${t(fieldName)} ${t('app.validations.number')}`))

            // Range (For Number)
            // example: ['range', '1-100'] = Range validation from 1 from 100
            if (validations.includes('range') && value) {
              const range = validations.find(validation => validation.includes('range|'))?.split('|')?.[1]
              if (range) {
                const breakRange = range.split('-')
                const min = parseInt(breakRange[0])
                const max = parseInt(breakRange[1])

                if (value >= min && value <= max) {
                  return Promise.resolve()
                } else {
                  return Promise.reject(new Error(`${t(fieldName)} ${t('app.validations.betweenRange')} ${min}-${max}`))
                }
              } else {
                return Promise.reject(new Error(t('app.validations.invalidRange')))
              }
            }
          }

          // Alpha Numeric
          if (validations.includes('alphaNumeric')) {
            if (value && !validation_alphaNumeric(value))
              return Promise.reject(new Error(`${t(fieldName)} ${t('app.validations.alphaNumeric')}`))
          }

          // Phone Number
          if (validations.includes('phoneNumber')) {
            if (value && !validation_phoneNumber(value))
              return Promise.reject(new Error(`${t(fieldName)} ${t('app.validations.notValid')}`))
          }

          // Code
          if (validations.includes('code')) {
            if (value && !validation_code(value))
              return Promise.reject(new Error(`${t(fieldName)} ${t('app.validations.notValid')}`))
          }

          // Decimal
          if (validations.includes('decimal')) {
            if (value && !validation_decimal(value))
              return Promise.reject(new Error(`${t(fieldName)} ${t('app.validations.number')}`))
          }

          // Do anything in the callback
          if (callback) callback(value, validatorCallback)

          return Promise.resolve()
        },
      })

      return rules
    },
    [
      t,
      validation_alphaNumeric,
      validation_code,
      validation_onlyNumber,
      validation_phoneNumber,
      validation_required,
      validation_validEmail,
      validation_decimal
    ],
  )

  return {
    validation_validate,
    validation_required,
    validation_validEmail,
    validation_onlyNumber,
    validation_alphaNumeric,
    validation_phoneNumber,
    validation_dropdownUnique,
    validation_decimal
  }
}

export { useValidation }
